import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  DropdownMenu,
  type DropdownMenuItemProps,
  IconButton,
  SEPARATOR_TITLE,
} from '@fly/core';
import { useFeaturesConfiguration } from '@fly/shared-hooks';

import {
  useArtifactDistributionDialog,
  useDeleteArtifactConfirmation,
} from '../../hooks';

export function ArtifactVersionActions({
  artifactVersion,
  packageType,
  name,
  showDelete = true,
}: {
  artifactVersion: string;
  packageType: string;
  name: string;
  /** When false, only distribution is offered (e.g. release artifacts list). Default true. */
  showDelete?: boolean;
}) {
  const { t } = useTranslation();
  const { distributionPackages } = useFeaturesConfiguration();
  const triggerDeleteArtifactConfirmation = useDeleteArtifactConfirmation();
  const { openArtifactDistributionDialog } = useArtifactDistributionDialog();

  const items = useMemo(() => {
    const inDistributionList = distributionPackages.includes(packageType);

    const deleteItem = {
      title: t('app.dashboard.artifacts.delete_artifact', {
        defaultValue: 'Delete Artifact',
      }),
      iconEnd: 'trash',
      onClick: () => {
        triggerDeleteArtifactConfirmation({
          name,
          version: artifactVersion,
          packageType,
        });
      },
    };

    const openDistributionForm = () => {
      openArtifactDistributionDialog({
        artifactName: name,
        versionName: artifactVersion,
        packageType,
      });
    };

    const distributionItem: DropdownMenuItemProps = {
      title: t('app.dashboard.artifacts.distribution_form.title', {
        defaultValue: 'Manage Distribution',
      }),
      iconEnd: 'distribution',
      onClick: openDistributionForm,
    };

    if (!showDelete) {
      if (!inDistributionList) {
        return [];
      }
      return [distributionItem];
    }

    if (!inDistributionList) {
      return [deleteItem];
    }

    return [
      deleteItem,
      {
        title: SEPARATOR_TITLE,
      },
      distributionItem,
    ];
  }, [
    artifactVersion,
    distributionPackages,
    name,
    openArtifactDistributionDialog,
    packageType,
    showDelete,
    t,
    triggerDeleteArtifactConfirmation,
  ]);

  if (items.length === 0) {
    return null;
  }

  return (
    <DropdownMenu
      trigger={
        <IconButton icon='dots_vertical' color='neutral' variant='ghost' />
      }
      items={items}
    />
  );
}
